import { NgOptimizedImage } from '@angular/common';
import { afterNextRender, ChangeDetectionStrategy, Component, inject, OnInit, signal, viewChild } from '@angular/core';
import { OnboardingCarousel } from '@app/shared/models';
import { TranslateService } from '@ngx-translate/core';
import { Bind } from 'primeng/bind';
import { Carousel } from 'primeng/carousel';

import { IconSvgComponent } from '@app/shared/icon-svg/icon-svg.component';

@Component({
    selector: 'ic-auth-carousel',
    templateUrl: './auth-carousel.component.html',
    styleUrls: ['./auth-carousel.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [Bind, Carousel, IconSvgComponent, NgOptimizedImage]
})
export class AuthCarouselComponent implements OnInit {
    private ts = inject(TranslateService);

    protected readonly slides = signal<OnboardingCarousel[]>([]);
    noWrapSlides = false;
    showIndicator = true;

    /**
     * How long each slide is held, in milliseconds, and 0 for "do not rotate".
     *
     * 5000 is the value this component has carried since PR 4572; it stopped having any effect when
     * the template moved to PrimeNG, because it was declared as an ngx-bootstrap CarouselConfig
     * provider that p-carousel never reads (AB#24634).
     *
     * 0 when the visitor has asked for reduced motion: p-carousel treats a falsy interval as
     * autoplay off (isAutoplay()), so the slides hold still and the indicator dashes keep working.
     * Sliding, self-advancing panels are the textbook case for that setting.
     */
    protected readonly autoplayIntervalMs = matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 5000;

    private readonly carousel = viewChild(Carousel);

    /** True between a pause and the matching resume. */
    private paused = false;

    /**
     * True once the visitor has chosen a slide themselves, after which the rotation stays off.
     *
     * PrimeNG means a dash click to stop autoplay for good - onDotClick calls stopAutoplay(), which
     * clears allowAutoplay - but stopAutoplay only does that while an interval is running, and the
     * hover or focus that preceded the click has already cleared it. Without this flag the next
     * pointerleave would start the rotation again on someone who had just picked a slide to read.
     */
    private visitorTookOver = false;

    constructor() {
        // PrimeNG's p-carousel positions itself and builds its dots in ngAfterContentChecked, but only once its
        // item container exists, and on the first check it does not exist yet. zone.js hid that by running more
        // checks straight away. Without zone.js there is no second check, so the carousel started on the clone of
        // the last slide with no dots. Handing it a new array after the first render refreshes this view, which
        // runs the carousel's content hook again now that its container is there (AB#23266).
        afterNextRender(() => this.slides.set([...this.slides()]));
    }

    /**
     * Hold the rotation while the visitor is reading, and start it again when they leave.
     *
     * WCAG 2.2.2 (Pause, Stop, Hide) asks for a way to stop content that moves on its own for more
     * than five seconds beside other content, which this panel does next to the sign-in form. Hover
     * covers the mouse; focusin and focusout cover the keyboard, since the active indicator dash is
     * the first focusable element on the page and carries tabindex 0.
     */
    protected pauseAutoplay() {
        this.paused = true;
        this.carousel()?.stopAutoplay(false);
    }

    protected resumeAutoplay() {
        this.paused = false;

        if (this.autoplayIntervalMs && !this.visitorTookOver) {
            this.carousel()?.startAutoplay();
        }
    }

    /**
     * p-carousel emits onPage for every page change, its own ticks included. While the rotation is
     * paused there are no ticks, so a page change can only have come from the visitor.
     */
    protected onPageChanged() {
        if (this.paused) {
            this.visitorTookOver = true;
        }
    }

    ngOnInit() {
        this.slides.set([
            {
                // Sized to the slot (AB#23860): the slide renders about 450 CSS px wide, so 500 px covers a
                // 1x screen and 1000 px a 2x one. The original was 1520x2010 and 518 KB, downloaded on every
                // sign-in page and scaled down about three times, which dev mode reports as NG0913.
                //
                // WebP, not PNG. The first attempt at this shipped 256-colour palette PNGs, which the old
                // 1520 px file also was - but at 1520 px the browser averaged about eleven source pixels into
                // each screen pixel and that averaging hid the banding. At 500 px in a 452 px slot there is
                // nothing left to average, so the same banding landed on screen as flat blotches across the
                // steel door. WebP keeps 24-bit colour and the alpha channel, and is smaller than the palette
                // PNGs it replaces: 40 KB against 45 KB, and 110 KB against 132 KB.
                img: 'assets/img/onboarding/controller-w-steel-body-500.webp',
                imgWidth: 500,
                imgHeight: 661,
                srcset: 'assets/img/onboarding/controller-w-steel-body-500.webp 1x, assets/img/onboarding/controller-w-steel-body-1000.webp 2x',
                title: this.ts.instant('HEADER.SIMPLIFIED_CONTROL_TITLE'),
                content: this.ts.instant('PARAGRAPHS.SIMPLIFIED_CONTROL_CONTENT'),
                class: 'simplified-control l-shadow',
                bgImg: 'assets/img/onboarding/golf_bg.jpg'
            },
            {
                img: 'assets/img/onboarding/iCentral_mobile.png',
                imgWidth: 885,
                imgHeight: 1214,
                title: this.ts.instant('HEADER.CONTROL_ANYWHERE_TITLE'),
                content: this.ts.instant('PARAGRAPHS.CONTROL_ANYWAY_CONTENT'),
                class: 'control-anywhere sxs-col',
                qrCode: 'proMaxQR'
            },
            {
                img: 'assets/img/onboarding/icentral-desktop-view.png',
                imgWidth: 1096,
                imgHeight: 593,
                img2: 'assets/img/onboarding/desktop.png',
                img2Width: 1123,
                img2Height: 870,
                title: this.ts.instant('HEADER.THE_NEXT_EVOLUTION_TITLE'),
                content: this.ts.instant('PARAGRAPHS.THE_NEXT_EVOLUTION_CONTENT'),
                class: 'next-evolution xl-shadow',
            }
        ]);
    }
}
